import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useRef } from 'react';
import Collapse from 'react-bootstrap/Collapse';
import { Anchor } from '../link/anchor.esm.js';
import { TagCategory } from '../tag-category/tag-category.esm.js';
import { Body } from '../text/body/body.esm.js';
import { Title } from '../text/title/title.esm.js';
import styles from './collapsible.module.scss.esm.js';

const testIds = {
    wrapper: 'collapsible-wrapper',
    title: 'collapsible-title',
    content: 'collapsible-content'
};
const Collapsible = ({ className, title, body, cta = null, children, eventKey, selected, media = null, noTopBorder, noBottomBorder, setSelected, disableCollapse, scrollToContentWhenExpanded, onClick, enableCloseOnBody = true, tag, ...rest })=>{
    const isExpanded = String(eventKey) === selected;
    const isActive = !(disableCollapse && isExpanded);
    const contentRef = useRef(null);
    const handleOnEntered = ()=>{
        if (scrollToContentWhenExpanded) {
            /**
             * Timeout is needed to allow the animation to complete
             * before scrolling into view.
             */ setTimeout(()=>{
                if (!contentRef.current) return;
                const headerOffset = 80;
                const contentPosition = contentRef.current.getBoundingClientRect().top;
                const offsetPosition = contentPosition + window.scrollY - headerOffset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }, 300);
        }
    };
    const handleOnClick = ()=>{
        if (disableCollapse && isExpanded) {
            return;
        }
        setSelected(`${isExpanded ? -1 : eventKey}`);
        onClick?.(!isExpanded);
    };
    const getTagVariant = (backgroundColor)=>{
        const colorMap = {
            'bg-tag-color-1': 'academy',
            'bg-tag-color-2': 'blog',
            'bg-tag-color-3': 'community'
        };
        return backgroundColor ? colorMap[backgroundColor] : 'guide';
    };
    return jsx("div", {
        ...rest,
        ref: contentRef,
        onClick: enableCloseOnBody ? handleOnClick : undefined,
        className: classNames(className, styles['collapsible-wrapper'], 'collapsible', {
            'cursor-pointer': isActive && enableCloseOnBody,
            'collapsible-expanded': isExpanded,
            'collapsible-no-top-border': noTopBorder,
            'collapsible-no-bottom-border': noBottomBorder,
            'collapsible-enable-close-on-body': enableCloseOnBody,
            'pt-300': isExpanded && cta,
            'py-300': !isExpanded || !cta
        }),
        "data-testid": testIds.wrapper,
        children: jsxs("div", {
            className: classNames(styles['collapsible-wrapper'], 'd-flex flex-column align-items-center'),
            children: [
                jsx("div", {
                    onClick: handleOnClick,
                    className: classNames('collapsible-title d-flex align-items-center justify-content-between w-100 gap-300 pe-50', {
                        'cursor-pointer': isActive
                    }),
                    "data-testid": testIds.title,
                    children: typeof title === 'string' ? jsxs(Fragment, {
                        children: [
                            jsxs(Title, {
                                className: "mb-0 d-inline-flex align-items-center gap-100",
                                render: "h5",
                                as: "h3",
                                children: [
                                    title,
                                    tag?.label && jsx(TagCategory, {
                                        children: tag.label,
                                        variant: getTagVariant(tag.backgroundColor)
                                    })
                                ]
                            }),
                            jsx(Anchor, {
                                className: classNames('collapsible-icon', {
                                    'disable pe-none': disableCollapse && isExpanded
                                }),
                                size: "md",
                                iconEnd: "ChevronDown",
                                "aria-label": "icon chevron down"
                            })
                        ]
                    }) : title
                }),
                jsx(Collapse, {
                    in: isExpanded,
                    onEnter: handleOnEntered,
                    children: jsxs("div", {
                        className: "w-100",
                        "data-testid": testIds.content,
                        children: [
                            jsx(Body, {
                                className: "pt-150",
                                children: body || children
                            }),
                            cta && jsx("div", {
                                onClick: (e)=>{
                                    e.stopPropagation();
                                },
                                className: classNames('py-300', styles['cta-wrapper']),
                                children: cta
                            }),
                            media
                        ]
                    })
                })
            ]
        })
    });
};

export { Collapsible, testIds };
